import React, { use } from 'react';

function defaultGetPreTagProps(
  props: React.ComponentPropsWithRef<'pre'>,
): React.ComponentPropsWithRef<'pre'> {
  return props;
}

const PreTagPropsContext = React.createContext(defaultGetPreTagProps);

/**
 * Component to be used as the `PreTag` prop of the `Prism` component.
 * Declaring a dynamic component inside of `PreTag` should be avoided, since
 * Prism will render this as `<PreTag>`, which means that every time React sees
 * a new reference in the `PreTag` prop, it will unmount the previous component,
 * and create a whole new DOM tree for the "new component". 
 * This is a big performance issue, as there might be thousands and thousands of 
 * child nodes.
 * 
 * Instead, use this `PreTag` component instead like this:
 * 
 * @example
 ```tsx
  <PreTag.DefineProps value={(props) => ({ ...props, className: 'my-custom-class' })}>
    <Prism
      language="graphql"
      PreTag={PreTag}
      {/* ...other props *\/}
    />
  </PreTag.DefineProps>
  ```
 */
export function PreTag({
  children,
  ...props
}: React.ComponentPropsWithoutRef<'pre'>) {
  const getPreTagProps = use(PreTagPropsContext);
  return (
    <pre {...getPreTagProps(props)}>
      <PreTagPropsContext value={defaultGetPreTagProps}>
        {children}
      </PreTagPropsContext>
    </pre>
  );
}

PreTag.DefineProps = PreTagPropsContext;
